import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { environment } from '../../environments/environment';

interface SsoCodeResponse {
  data: { code: string; redirectUri: string };
}

interface SsoError {
  icon: string;
  title: string;
  detail: string;
  canRetry: boolean;
}

@Component({
  selector: 'app-sso-crm',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './sso-crm.component.html',
  styleUrl: './sso-crm.component.scss'
})
export class SsoCrmComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #http = inject(HttpClient);

  error = signal<SsoError | null>(null);

  ngOnInit(): void {
    const params = this.#route.snapshot.queryParamMap;
    const clientId = params.get('client_id');
    const redirectUri = params.get('redirect_uri');
    const state = params.get('state');
    const codeChallenge = params.get('code_challenge');

    // Opened from the CRM tile: the CRM starts the sign-in and comes back here with its parameters.
    if (!clientId && !redirectUri) {
      window.location.replace(environment.crmUrl);
      return;
    }

    if (!clientId || !redirectUri || !state || !codeChallenge) {
      this.error.set({
        icon: 'bi-link-45deg',
        title: 'This link is incomplete',
        detail: 'Open the CRM again from the portal.',
        canRetry: false
      });
      return;
    }

    this.#http
      .post<SsoCodeResponse>(`${environment.baseUrlV2}/sso/code`, {
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: params.get('code_challenge_method') ?? 'S256'
      })
      .subscribe({
        next: response => {
          const target = new URL(response.data.redirectUri);
          target.searchParams.set('code', response.data.code);
          target.searchParams.set('state', state);
          window.location.replace(target.toString());
        },
        error: (error: HttpErrorResponse) =>
          this.error.set(
            error.status === 403
              ? {
                  icon: 'bi-shield-lock',
                  title: 'No access to the CRM',
                  detail: 'Your account does not have a CRM role. Ask an administrator to give you one.',
                  canRetry: false
                }
              : {
                  icon: 'bi-exclamation-circle',
                  title: 'Could not open the CRM',
                  detail: 'Something went wrong while signing you in. Please try again.',
                  canRetry: true
                }
          )
      });
  }

  retry(): void {
    window.location.replace(environment.crmUrl);
  }
}
